/**
 * @file Mi plan (Billing) — restyled to match design page-plan.jsx.
 *
 * Layout:
 *  - pg-head: title + sub
 *  - plan-current-card: gradient hero with plan name + price + usage bar + features
 *  - plan-options: comparison grid (Gratis / Pro)
 *  - factura-card: fiscal data CTA (opens fiscal accordion below)
 *  - Fiscal accordion (RFC, razón social, régimen, uso CFDI, CP, constancia)
 *
 * All Stripe / fiscal / trial logic preserved from the previous implementation.
 */
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, X, Crown, Share2, User, Upload, Receipt, AlertTriangle } from "lucide-react";
import { useSubscription } from "../hooks/useSubscription";
import { professionalService } from "../services";
import { SUBSCRIPTION_STRINGS as S } from "../constants/strings";
import { Dialog, DialogContent, DialogBody, Input, Select, SelectItem, toast } from "../ui";

const REGIMEN_OPTIONS = [
  { value: "601", label: "601. General de Ley Personas Morales" },
  { value: "603", label: "603. Personas Morales con Fines no Lucrativos" },
  { value: "605", label: "605. Sueldos y Salarios" },
  { value: "606", label: "606. Arrendamiento" },
  { value: "608", label: "608. Demás ingresos" },
  { value: "612", label: "612. Actividades Empresariales y Profesionales" },
  { value: "616", label: "616. Incorporación Fiscal" },
  { value: "626", label: "626. Régimen Simplificado de Confianza (RESICO)" },
];

const USO_CFDI_OPTIONS = [
  { value: "G01", label: "G01. Adquisición de mercancías" },
  { value: "G03", label: "G03. Gastos en general" },
  { value: "I04", label: "I04. Equipo de cómputo" },
  { value: "D01", label: "D01. Honorarios médicos y gastos hospitalarios" },
  { value: "S01", label: "S01. Sin efectos fiscales" },
];

const PLAN_LABELS = { GRATIS: "Gratis", TRIAL: "Prueba Pro", PRO: "Pro", PRO_PLUS: "Pro" };

const PLAN_FEATURES_DEFAULT = [
  "Citas según tu plan",
  "Ubicaciones según tu plan",
  "Notificaciones por email",
  "Perfil público completo",
  "Estadísticas y dashboard",
  "Gestión de pacientes",
  "Listado de servicios",
  "Recordatorios automáticos",
];

export default function Billing() {
  const {
    status,
    isLoading,
    effectiveTier,
    proTrialRemaining,
    isPro,
    isOnTrial,
    isFree,
    canActivateTrial,
    trialExpired,
    startCheckout,
    openPortal,
    activateTrial,
    fetchStatus,
  } = useSubscription();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [showTrialSuccess, setShowTrialSuccess] = useState(false);
  const [showSubSuccess, setShowSubSuccess] = useState(null);
  const [fiscalOpen, setFiscalOpen] = useState(false);

  const slug = status?.slug;
  const profileUrl = slug
    ? `${import.meta.env.VITE_PUBLIC_SITE_URL || window.location.origin}/c/${slug}`
    : null;

  const handleShareProfile = async () => {
    if (!profileUrl) return;
    if (navigator.share) {
      try { await navigator.share({ url: profileUrl }); } catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(profileUrl); toast.success(S.TRIAL_SUCCESS_LINK_COPIED); }
      catch { /* ignore */ }
    }
  };

  const handleStartCheckout = (tier) => {
    localStorage.setItem("checkoutTier", tier);
    startCheckout(tier);
  };

  const handleActivateTrial = async () => {
    const ok = await activateTrial();
    if (ok) setShowTrialSuccess(true);
  };

  const [fiscal, setFiscal] = useState({
    rfc: "", razonSocial: "", regimenFiscal: "", usoCfdi: "", codigoPostalFiscal: "",
  });
  const [fiscalSaving, setFiscalSaving] = useState(false);
  const [constanciaFile, setConstanciaFile] = useState(null);
  const [constanciaUploaded, setConstanciaUploaded] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (searchParams.get("success") === "true") {
      const tier = localStorage.getItem("checkoutTier") || "PRO";
      localStorage.removeItem("checkoutTier");
      setShowSubSuccess(tier);
      const t = setTimeout(() => fetchStatus(), 2000);
      return () => clearTimeout(t);
    } else if (searchParams.get("cancelled") === "true") {
      toast.info("Proceso de pago cancelado");
    }
  }, [searchParams, fetchStatus]);

  useEffect(() => {
    if (status?.fiscalData) {
      setFiscal({
        rfc: status.fiscalData.rfc || "",
        razonSocial: status.fiscalData.razonSocial || "",
        regimenFiscal: status.fiscalData.regimenFiscal || "",
        usoCfdi: status.fiscalData.usoCfdi || "",
        codigoPostalFiscal: status.fiscalData.codigoPostalFiscal || "",
      });
      if (status.fiscalData.constanciaUrl) setConstanciaUploaded(true);
    }
  }, [status?.fiscalData]);

  const handleFiscalChange = (field) => (e) => {
    let value = typeof e === "string" ? e : e.target.value;
    if (field === "rfc") value = value.toUpperCase().replace(/[^A-ZÑ&0-9]/g, "").slice(0, 13);
    if (field === "codigoPostalFiscal") value = value.replace(/\D/g, "").slice(0, 5);
    if (field === "razonSocial") value = value.toUpperCase();
    setFiscal((prev) => ({ ...prev, [field]: value }));
  };

  const handleFiscalSave = async () => {
    setFiscalSaving(true);
    try {
      await professionalService.saveFiscalData(fiscal);
      if (constanciaFile) {
        const formData = new FormData();
        formData.append("constancia", constanciaFile);
        await professionalService.uploadConstancia(formData);
        setConstanciaUploaded(true);
        setConstanciaFile(null);
      }
      toast.success(S.FISCAL_SAVED);
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.message || S.FISCAL_SAVE_ERROR);
    } finally {
      setFiscalSaving(false);
    }
  };

  const fiscalComplete =
    fiscal.rfc && fiscal.razonSocial && fiscal.regimenFiscal && fiscal.usoCfdi && fiscal.codigoPostalFiscal;

  if (isLoading) {
    return <div className="pg-empty" style={{ padding: 60 }}>Cargando…</div>;
  }

  const appointments = status?.appointments || {};
  const used = appointments.current || 0;
  const limit = appointments.limit;
  const isUnlimited = !limit || limit === Infinity;
  const pct = isUnlimited ? 0 : limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const planName = PLAN_LABELS[effectiveTier];
  const planPrice = isPro ? 399 : 0;

  const billingDay = status?.billingDay || 1;
  const now = new Date();
  const resetThisMonth = new Date(now.getFullYear(), now.getMonth(), billingDay);
  const nextReset = now >= resetThisMonth
    ? new Date(now.getFullYear(), now.getMonth() + 1, billingDay)
    : resetThisMonth;
  const resetLabel = nextReset.toLocaleDateString("es-MX", { day: "numeric", month: "long" });

  return (
    <>
      <div className="pg-head">
          <div>
            <div className="cd-eyebrow">Suscripción y facturación</div>
            <h1 className="pg-title">Mi <em>plan</em></h1>
            <p className="pg-sub">Revisa tu uso, gestiona tu suscripción y descarga facturas.</p>
          </div>
        </div>

        {trialExpired && (
          <div
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "12px 14px", borderRadius: 9, marginBottom: 18,
              background: "oklch(0.96 0.05 60)", color: "oklch(0.45 0.13 50)",
              fontSize: 13.5,
            }}
          >
            <AlertTriangle size={16} />
            <span>{S.TRIAL_EXPIRED}</span>
          </div>
        )}

        {/* Plan current card (gradient hero) */}
        <div className="plan-current-card">
          <div className="plan-head">
            <div>
              <div className="lbl">Plan actual</div>
              <h2>{planName}</h2>
            </div>
            <div className="price">
              {isPro && (
                <span style={{ textDecoration: "line-through", opacity: 0.55, fontWeight: 400, fontSize: "0.6em", marginRight: 6 }}>$999</span>
              )}
              ${planPrice} <span className="small">MXN/mes</span>
            </div>
          </div>
          {!isUnlimited && (
            <div className="plan-usage">
              <div className="row">
                <span style={{ color: "rgba(255,255,255,0.85)" }}>Uso este mes</span>
                <span className="num">{used} de {limit} citas</span>
              </div>
              <div className="bar">
                <div className="fill" style={{ width: pct + "%" }} />
              </div>
              <div className="reset">Se reinicia el {resetLabel}</div>
            </div>
          )}
          <div className="plan-features-grid">
            {PLAN_FEATURES_DEFAULT.map((t, i) => (
              <div key={i} className="plan-feature">
                <span className="ic"><CheckCircle2 size={16} /></span>
                <span>{t}</span>
              </div>
            ))}
          </div>
          <div className="plan-actions">
            {isPro && (
              <button type="button" onClick={openPortal}>Administrar suscripción</button>
            )}
            {canActivateTrial && (
              <button type="button" onClick={handleActivateTrial}>Activar prueba Pro</button>
            )}
            {isFree && (
              <button type="button" onClick={() => handleStartCheckout("PRO")}>Sube a Pro</button>
            )}
          </div>
        </div>

        {/* Plan options */}
        {!isPro && (
          <>
            <div style={{ marginBottom: 14, marginTop: 24 }}>
              <h2 className="cd-section-title" style={{ marginBottom: 4 }}>
                Mejora a <em>Pro</em>
              </h2>
              <div className="cd-section-meta">Compara y cambia cuando quieras.</div>
            </div>
            <div className="plan-options">
              <PlanOption
                name="Gratis"
                price={0}
                desc="Para empezar a recibir citas."
                features={[
                  { on: true, txt: "30 citas por mes" },
                  { on: true, txt: "1 ubicación" },
                  { on: true, txt: "Notificaciones por email" },
                  { on: false, txt: "WhatsApp a pacientes" },
                  { on: false, txt: "Múltiples ubicaciones" },
                ]}
                cta={isFree ? "Plan actual" : "Bajar a Gratis"}
                ctaStyle="ghost"
                current={isFree}
                onClick={() => isFree ? null : openPortal()}
              />
              <PlanOption
                name="Pro"
                price={399}
                listPrice={999}
                desc="Para profesionales con consulta activa."
                features={[
                  { on: true, txt: "Citas ilimitadas" },
                  { on: true, txt: "Ubicaciones ilimitadas" },
                  { on: true, txt: "WhatsApp a pacientes" },
                  { on: true, txt: "Recordatorios automáticos" },
                  { on: true, txt: "Soporte prioritario" },
                ]}
                cta="Activar Pro"
                ctaStyle=""
                recommended
                onClick={() => handleStartCheckout("PRO")}
              />
            </div>
          </>
        )}

        {/* Fiscal accordion */}
        <div className="factura-card" style={{ marginTop: 24 }}>
          <div className="ic"><Receipt size={20} /></div>
          <div className="copy">
            <h5>¿Necesitas factura?</h5>
            <p>Proporciona tus datos fiscales para que podamos emitir tus facturas (CFDI).</p>
          </div>
          <button
            type="button"
            className="pg-btn solid"
            onClick={() => setFiscalOpen((v) => !v)}
          >
            {fiscalOpen ? "Ocultar" : "Configurar datos fiscales"}
          </button>
        </div>

        {fiscalOpen && (
          <div className="pg-table-card" style={{ marginTop: 16, padding: 24 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <Input
                label={S.FISCAL_RFC}
                value={fiscal.rfc}
                onChange={handleFiscalChange("rfc")}
                hint={S.FISCAL_RFC_HELPER}
                maxLength={13}
              />
              <Input
                label={S.FISCAL_CP}
                value={fiscal.codigoPostalFiscal}
                onChange={handleFiscalChange("codigoPostalFiscal")}
                hint={S.FISCAL_CP_HELPER}
                maxLength={5}
              />
              <div style={{ gridColumn: "1 / -1" }}>
                <Input
                  label={S.FISCAL_RAZON_SOCIAL}
                  value={fiscal.razonSocial}
                  onChange={handleFiscalChange("razonSocial")}
                  hint={S.FISCAL_RAZON_SOCIAL_HELPER}
                />
              </div>
              <Select
                label={S.FISCAL_REGIMEN}
                value={fiscal.regimenFiscal}
                onValueChange={(v) => setFiscal((p) => ({ ...p, regimenFiscal: v }))}
                placeholder="Selecciona régimen"
              >
                {REGIMEN_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </Select>
              <Select
                label={S.FISCAL_USO_CFDI}
                value={fiscal.usoCfdi}
                onValueChange={(v) => setFiscal((p) => ({ ...p, usoCfdi: v }))}
                placeholder="Selecciona uso CFDI"
              >
                {USO_CFDI_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </Select>
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "flex", alignItems: "center", gap: 12,
                  padding: 14, borderRadius: 9, cursor: "pointer",
                  border: `1px dashed ${constanciaUploaded ? "var(--ok)" : "var(--line-strong)"}`,
                  background: constanciaUploaded ? "oklch(0.97 0.04 155)" : "var(--bg-tint)",
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setConstanciaFile(e.target.files[0]);
                      setConstanciaUploaded(false);
                    }
                  }}
                />
                {constanciaUploaded && !constanciaFile ? <CheckCircle2 size={18} color="var(--ok)" /> : <Upload size={18} />}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{S.FISCAL_CONSTANCIA}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                    {constanciaFile ? constanciaFile.name : constanciaUploaded ? S.FISCAL_CONSTANCIA_UPLOADED : S.FISCAL_CONSTANCIA_HELPER}
                  </div>
                </div>
              </div>
            </div>
            <button
              type="button"
              className="pg-btn solid"
              onClick={handleFiscalSave}
              disabled={!fiscalComplete || fiscalSaving}
              style={{ marginTop: 16, width: "100%", padding: "12px", fontSize: 14 }}
            >
              {fiscalSaving ? "Guardando…" : S.FISCAL_SAVE}
            </button>
          </div>
        )}

      {/* Trial success dialog */}
      <Dialog open={showTrialSuccess} onOpenChange={setShowTrialSuccess}>
        <DialogContent size="sm" showClose={false}>
          <div style={{ padding: "32px 28px 24px", textAlign: "center" }}>
            <Crown size={44} style={{ color: "var(--accent)", marginBottom: 12 }} />
            <h3 style={{ fontFamily: "var(--serif)", fontSize: 24, margin: 0 }}>
              {S.TRIAL_SUCCESS_TITLE}
            </h3>
            <p style={{ color: "var(--ink-3)", marginTop: 8 }}>{S.TRIAL_SUCCESS_SUBTITLE}</p>
          </div>
          <DialogBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {profileUrl ? (
                <button type="button" className="pg-btn solid" style={{ width: "100%" }} onClick={handleShareProfile}>
                  <Share2 size={14} /> {S.TRIAL_SUCCESS_SHARE}
                </button>
              ) : (
                <button
                  type="button"
                  className="pg-btn solid"
                  style={{ width: "100%" }}
                  onClick={() => { setShowTrialSuccess(false); navigate("/mi-perfil"); }}
                >
                  <User size={14} /> {S.TRIAL_SUCCESS_COMPLETE_PROFILE}
                </button>
              )}
              <button
                type="button"
                className="pg-btn"
                style={{ width: "100%" }}
                onClick={() => setShowTrialSuccess(false)}
              >
                {S.TRIAL_SUCCESS_CLOSE}
              </button>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Subscription success dialog */}
      <Dialog open={!!showSubSuccess} onOpenChange={(v) => !v && setShowSubSuccess(null)}>
        <DialogContent size="sm" showClose={false}>
          <div style={{ padding: "32px 28px 24px", textAlign: "center" }}>
            <Crown size={44} style={{ color: "var(--accent)", marginBottom: 12 }} />
            <h3 style={{ fontFamily: "var(--serif)", fontSize: 24, margin: 0 }}>
              {S.SUBSCRIPTION_SUCCESS_TITLE_PRO}
            </h3>
            <p style={{ color: "var(--ink-3)", marginTop: 8 }}>{S.SUBSCRIPTION_SUCCESS_SUBTITLE}</p>
          </div>
          <DialogBody>
            <button
              type="button"
              className="pg-btn solid"
              style={{ width: "100%", marginBottom: 8 }}
              onClick={() => setShowSubSuccess(null)}
            >
              {S.TRIAL_SUCCESS_CLOSE}
            </button>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PlanOption({ name, price, listPrice, desc, features, cta, ctaStyle, recommended, current, onClick }) {
  return (
    <div className={"plan-option" + (recommended ? " recommended" : "")}>
      {recommended && <span className="badge-rec">Recomendado</span>}
      {current && <span className="badge-cur">Plan actual</span>}
      <h4>{name}</h4>
      <div className="desc">{desc}</div>
      <div className="price-line">
        {listPrice && listPrice > price && (
          <span className="p" style={{ textDecoration: "line-through", opacity: 0.5, fontWeight: 400, marginRight: 6 }}>${listPrice}</span>
        )}
        <span className="p">${price}</span>
        <span className="per">{price === 0 ? " — gratis" : " MXN/mes"}</span>
      </div>
      <ul>
        {features.map((f, i) => (
          <li key={i} className={f.on ? "" : "dim"}>
            {f.on ? <CheckCircle2 size={14} /> : <X size={14} />}
            {f.txt}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className={ctaStyle}
        onClick={onClick}
        disabled={current}
        style={current ? { opacity: 0.6, cursor: "not-allowed" } : undefined}
      >
        {cta}
      </button>
    </div>
  );
}
